import { useMemo } from "react";

import { differenceInCalendarDays } from "date-fns";

import { useEpisodes } from "@/contexts/EpisodesContext";
import { useMentionCards } from "@/contexts/MentionCardsContext";
import { Episode } from "@/types/Episode";
import { MentionCard } from "@/types/MentionCard";

// Improve type safety
type DateBreakpointResult = Record<number, string> | { error: unknown };

// This function calculates the number of days between the current date and the published date of each mention.
// It assigns a corresponding label based on the difference in days.
const getDaysFrom = <T extends boolean>(
  episodes: T extends true ? MentionCard[] : Episode[],
  v4: T
): DateBreakpointResult => {
  const now = new Date().setHours(12);
  const indices: Record<number, string> = {};
  // Array of breakpoints that define different spans of days and their corresponding labels.
  // It could be a constant outside of this function, but since we want to delete some of them as we use them,
  // we would have to make a copy of it inside the function anyway.
  const breakpoints = [
    {
      is: (d: number) => d > 6,
      label: L.MENTION_LIST_BREAKPOINT_EARLIER,
    },
    {
      is: (d: number) => d > 1 && d < 7,
      label: L.MENTION_LIST_BREAKPOINT_LAST_WEEK,
    },
    {
      is: (d: number) => d === 1,
      label: L.MENTION_LIST_BREAKPOINT_YESTERDAY,
    },
    {
      is: (d: number) => d === 0,
      label: L.MENTION_LIST_BREAKPOINT_TODAY,
    },
  ];

  try {
    episodes.forEach((mention, index) => {
      let date;
      if (v4) {
        date = new Date((mention as MentionCard).episode.published_date);
      } else {
        date = new Date((mention as Episode).post_data.published_date);
      }

      const daysAgo = differenceInCalendarDays(now, date);
      const at = breakpoints.findIndex((breakpoint) => breakpoint.is(daysAgo));

      // If no matching breakpoint is found, skip this mention.
      if (at === -1) return;

      // Assign the label of the found breakpoint to the mention.
      indices[index] = breakpoints[at].label;

      // Remove the used breakpoint from the array to prevent its reuse.
      breakpoints.splice(at, 1);
    });

    return indices;
  } catch (error: unknown) {
    return { error };
  }
};

const useDateBreakPoints = ({ v4 = false }: { v4?: boolean }) => {
  const { episodes: legacyEpisodes } = useEpisodes();
  const { mentionCards } = useMentionCards();

  const episodes = useMemo(
    () => (v4 ? mentionCards : legacyEpisodes),
    [v4, legacyEpisodes, mentionCards]
  );

  // Derive the indices (date breakpoints) from the episodes data.
  const indices = useMemo<Record<number, string>>(
    () => getDaysFrom(episodes, v4),
    [episodes, v4]
  );

  return indices;
};

export default useDateBreakPoints;
